import { SolEstimateTableRowBaseExtendedSubContractor } from './../../models/sol-estimate-table/SolEstimateSubContractorCostsRow';
import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { SolEstimateTableComponent } from "../sol-estimate-table/sol-estimate-table.component";
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SolEstimateTotalsComponent } from '../sol-estimate-totals/sol-estimate-totals.component';
import { CommonDataService } from '../../services/common-data.service';
import { Subject, Subscription, merge } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { SolEstimateService } from '../../services/estimate/sol-estimate.service';
import { EstimateCommonFunctionsService } from '../../services/estimate/estimate-common-functions.service';
import { SolEstimateTableCommonFunctionsService } from '../../services/sol-estimate-table-common-functions/sol-estimate-table-common-functions.service';
import { EscalationResetService } from '../../services/operations/escalation-reset.service';
import { NotificationMessageService } from '../../services/common/notification-message.service';
import { NameValidationService } from '../../services/name-validation.service';
import { CONSTANTS } from '../../Constants/Constants';
import { Escalations } from '../../models/dtos';
import { SolEstimatePricingMode } from '../../models/enum';
import { SolEstimateFormGroup } from '../../models/sol-estimate-table/SolEstimateFormGroup';
import { SolEstimateTableRowBase } from '../../models/sol-estimate-table/SolEstimateTableRowBase';
import { SolEstimateTableRowBaseExtended } from '../../models/sol-estimate-table/SolEstimateSubContractorCostsRow';
import { SolEstimateTableMaterialCostsRowCollection } from '../../models/sol-estimate-table/SolEstimateTableMaterialCostsRowCollection';
import { SolEstimateTableMaterialCostsRow } from '../../models/sol-estimate-table/SolEstimateTableMaterialCostsRow';
import { SolEstimateTableLaborCosts } from '../../models/sol-estimate-table/SolEstimateTableLaborCosts';
import { SolEstimateTableSubContractorsCosts } from '../../models/sol-estimate-table/SolEstimateTableSubContractorsCosts';
import { SolEstimateTableMiscRiskTravelCosts } from '../../models/sol-estimate-table/SolEstimateTableMiscRiskTravelCosts';
import { SolEstimateTableSellAsGroup } from '../../models/sol-estimate-table/SolEstimateTableSellAsGroup';
import { SolEstimateTableSellAsBase } from '../../models/sol-estimate-table/SolEstimateTableSellAsBase';
import { ShippingHandlingData } from '../../models/sol-estimate-table/ShippingHandlingData';
import { WarrantyDialogResult } from '../../models/sol-estimate-table/WarrantyData';
import { SolMaterialEscalationDialogDataModel } from '../../models/sol-estimate-table/SolMaterialEscalationDialogDataModel';
import { SolEstimateRiskData } from '../../models/sol-estimate-table/SolEstimateRiskData';
import { SolEstimateTableTaxRate, SolEstimateTableTaxRateHeaderRow, SolEstimateTableTaxRateRow } from '../../models/sol-estimate-table/SolEstimateTableTaxRate';
import {
  EstimateLaborCostsModel,
  EstimateMaterialCostsModel,
  EstimateMaterialTypeModel,
  EstimateMiscRiskTravelCostsModel,
  EstimateRowExtendedModel,
  EstimateRowModel,
  EstimateSellAsGroupModel,
  EstimateSubContractorCostsModel,
  EstimateTaxRateModel,
  EstimateUiModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { TrackChangesDirective } from '../../directives/track-changes.directive';
import { EstimateLastModifiedSellPriceService } from '../../services/estimate/estimate-last-modified-sell-price.service';
import { EstimateRowExtendedModelSubContractor } from '../../models/sol-estimate-table/ui-models/estimate-row.model';

@Component({
  selector: 'app-estimates',
  imports: [
    MatFormFieldModule,
    MatLabel,
    MatInputModule,
    FormsModule,
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    SolEstimateTableComponent,
    MatSlideToggleModule,
    ReactiveFormsModule,
    TranslatePipe,
    SolEstimateTotalsComponent,
    TrackChangesDirective
  ],
  templateUrl: './estimate.component.html',
  styleUrl: './estimate.component.scss',
})
export class EstimateComponent implements OnInit, OnDestroy {

  /**
   * The component owns the FormGroup.
   * Created directly from the EstimateUiModel provided by the service.
   */
  estimateFormGroup!: FormGroup<SolEstimateFormGroup>;

  isMarkUpSelected: boolean = false;
  destroy$: Subject<void> = new Subject<void>();
  escalationTotal: number = 0;
  private selectionControl!: FormControl<string>;
  private descriptionControl!: FormControl<string>;
  private previousValidEstimateName = CONSTANTS.EMPTY_STRING;
  private previousValidDescription = CONSTANTS.EMPTY_STRING;
  /** The labor-cost row keys (excludes the header). Shared across setup, patch, and read methods. */
  private readonly laborKeys: Exclude<keyof EstimateLaborCostsModel, 'laborCostsHeader'>[] = [
    'projectManagement', 'engineering', 'networkEngineering', 'qualificationEngineering',
    'cyberSecurityEngineering', 'energyEngineering', 'electricalEngineering',
    'graphics', 'programming', 'specialist', 'digitalDeploymentSpecialist',
    'mechanical', 'electrical'
  ];

  /** Tracks per-row subscriptions so they can be torn down on mode changes. */
  private rowSubscriptions: Subscription[] = [];

  /** Tracks tax-rate subscriptions separately — they must survive pricing mode changes. */
  private taxRateSubscriptions: Subscription[] = [];

  /** Guard flag to suppress otherMetaDataChanged$ processing when the emission is self-triggered. */
  private suppressMetaDataReaction = false;

  constructor(
    private readonly commonDataService: CommonDataService,
    readonly solEstimateService: SolEstimateService,
    private readonly fb: NonNullableFormBuilder,
    private readonly solEstimateTableCommonFunctionsService: SolEstimateTableCommonFunctionsService,
    private readonly escalationResetService: EscalationResetService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly nameValidationService: NameValidationService,
    private readonly estimateLastModifiedSellPriceService: EstimateLastModifiedSellPriceService,
    private readonly translate: TranslateService,
    private readonly estimateCommonFunctionsService: EstimateCommonFunctionsService
  ) { }

  ngOnInit() {
    // Build the form from the model as it currently stands - defaults while no estimate is open -
    // so the table renders empty and every subscription below binds to controls that already exist.
    this.estimateFormGroup = this.createFormGroupFromModel(this.solEstimateService.estimateModel);
    this.selectionControl = this.estimateFormGroup.controls.selectionControl;
    this.descriptionControl = this.estimateFormGroup.controls.descriptionControl;
    this.previousValidEstimateName = this.selectionControl.value ?? CONSTANTS.EMPTY_STRING;
    this.previousValidDescription = this.descriptionControl.value ?? CONSTANTS.EMPTY_STRING;
    this.subscribeToEstimateNameAndDescChanges();
    this.setupFormEnabledState();
    this.setupGlobalRecalcSubscriptions();
    this.setupPerRowRecalcSubscriptions();
    this.setupTaxRateSubscriptions();
    this.setupShippingDataSubscription();
    this.setupWarrantyDataSubscription();
    this.setupEscalationDataSubscription();
    this.setupEscalationResetSubscription();
    this.setupRiskDataSubscription();
    this.setupExternalModelUpdateSubscription();
    this.setupPropertiesGmPercentSubscription();
    this.suppressMetaDataReaction = true;
    this.setupTaxMetaDataChangeSubscription();
    this.suppressMetaDataReaction = false;
    this.setupSapSubmittedSubscription();
    this.escalationTotal = this.calculateEscalationTotal();
    // Subscribed last so the form and every handler above are ready before any estimate data arrives.
    this.setupEstimateOpenedSubscription();
    if (this.escalationResetService?.isEscalationResetFromReview) {
      this.escalationResetService.notifyEscalationReset();
      this.escalationResetService.isEscalationResetFromReview = false;
    }
  }

  /**
   * Loads the opened estimate into the form built by ngOnInit. Replays on subscribe, so it also
   * covers the case where the estimate was already open before this component was created.
   */
  private setupEstimateOpenedSubscription(): void {
    this.commonDataService.projectObjectDataAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpened: boolean) => {
        if (isOpened) {
          this.initializeEstimateData();
        }
      });
  }

  /**
   * Populates the existing FormGroup from the opened estimate's metadata. The form instance and
   * all global/service subscriptions are reused; only the row and tax-rate subscriptions are
   * rebuilt, because patching replaces the FormGroups inside those variable-length FormArrays.
   */
  private initializeEstimateData(): void {
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const estimateTab = estimateMetaData?.estimateTab ?? null;
    const useAutoRisk = estimateMetaData?.estimateSettings?.useAutoRisk ?? false;

    const model = this.solEstimateService.patchEstimateModelValues(estimateTab);
    this.solEstimateService.setShowRiskCostsAccordion(useAutoRisk);

    this.isMarkUpSelected = model.markUpToggle;
    this.patchFormFromModel(model);

    this.setupPerRowRecalcSubscriptions();
    this.resetTaxRateSubscriptions();

    // Re-applied because patching pushes new row FormGroups, which are always created enabled.
    this.applyFormEnabledState();
    this.escalationTotal = this.calculateEscalationTotal();
  }
  /**
   * Subscribes to otherMetaData changes (e.g. tax rates or the technology fee toggle changed from Properties).
   * Triggers a tax recalculation and patches the form so the UI stays in sync.
   * Tears down and rebuilds tax rate subscriptions because patchTaxRate may rebuild the FormArray.
   */
  private setupTaxMetaDataChangeSubscription(): void {
    this.commonDataService.otherMetaDataChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.suppressMetaDataReaction) {
          return;
        }

        const updatedModel = this.solEstimateService.recalculateFromMetaDataChange();
        this.patchFormFromModel(updatedModel);

        // Re-subscribe because patchTaxRate may have rebuilt the tax rate FormArray.
        this.resetTaxRateSubscriptions();
      });
  }

  /**
   * Subscribes to external model updates (e.g. when the cost calculation pipeline
   * triggers a full recalculation from outside the estimate component).
   * Patches the form with the updated model so the UI stays in sync.
   */
  private setupExternalModelUpdateSubscription(): void {
    this.solEstimateService.onEstimateModelUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((updatedModel: EstimateUiModel) => {
        this.isMarkUpSelected = updatedModel.markUpToggle;
        this.escalationTotal = this.calculateEscalationTotal();
        this.patchFormFromModel(updatedModel);
      });
  }

  /**
   * Subscribes to shipping data updates from the shipping dialog.
   * Patches the model via the service and syncs the updated model to the form.
   */
  private setupShippingDataSubscription(): void {
    this.solEstimateTableCommonFunctionsService.updatedShippingData
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ShippingHandlingData[]) => {
        if (data && data.length > CONSTANTS.ZERO) {
          this.solEstimateService.updateShippingData(data);
          this.patchFormFromModel(this.solEstimateService.estimateModel);
        }
      });
  }

  /**
   * Escalation Total calculation
   */
  calculateEscalationTotal(): number {
    const escalationRates: Escalations = this.commonDataService.getEstimateMetaData()?.escalationRates;
    if (!escalationRates || this.commonDataService.getRootNodeHidden()) {
      return 0;
    }

    return this.estimateCommonFunctionsService.calculateEscalationTotal(escalationRates);
  }

  /**
   * Subscribes to warranty data updates from the warranty dialog.
   * Patches the model via the service and syncs the updated model to the form.
   */
  private setupWarrantyDataSubscription(): void {
    this.solEstimateTableCommonFunctionsService.updatedWarrantyData
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: WarrantyDialogResult) => {
        if (result.data && result.data.length > CONSTANTS.ZERO) {
          this.solEstimateService.updateWarrantyData(result.data, result.locked);
          this.patchFormFromModel(this.solEstimateService.estimateModel);
        }
      });
  }

  /**
   * Subscribes to escalation data updates from the escalation dialog.
   * Patches the model via the service and syncs the updated model to the form.
   */
  private setupEscalationDataSubscription(): void {
    this.solEstimateTableCommonFunctionsService.updatedEscalationData
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: SolMaterialEscalationDialogDataModel) => {
        this.solEstimateService.updateEscalationData(result);
        this.patchFormFromModel(this.solEstimateService.estimateModel);
        this.escalationTotal = this.calculateEscalationTotal();
      });
  }

  /**
   * Subscribes to escalation reset events (CRM sync, FY change, properties date change).
   * The reset service has already re-run the cost cascade, so only the view is synced here.
   */
  private setupEscalationResetSubscription(): void {
    this.escalationResetService.escalationReset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.patchFormFromModel(this.solEstimateService.estimateModel);
        this.escalationTotal = this.calculateEscalationTotal();
      });
  }

  /**
   * Subscribes to risk data updates from the Risk dialog.
   * Updates model via service and syncs updated model to the form.
   */
  private setupRiskDataSubscription(): void {
    this.solEstimateTableCommonFunctionsService.updatedRiskData
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (riskData: SolEstimateRiskData) => {
          this.solEstimateService.updateRiskData(riskData);
          this.patchFormFromModel(this.solEstimateService.estimateModel);
        },
        error: (error: any) => {
          console.error('Error updating risk data:', error);
        }
      });
  }

  setIsMarkUpSelected(isSelected: boolean) {
    this.isMarkUpSelected = isSelected;
  }

  private setupFormEnabledState(): void {
    merge(
      this.commonDataService.estimateDataChanged$,
      this.commonDataService.readOnlyMode$
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFormEnabledState());
  }

  private applyFormEnabledState(): void {
    const isEstimateOpen = !!this.commonDataService.getEstimateData();
    const isEditable = isEstimateOpen && !this.commonDataService.isReadOnlyMode();

    if (isEditable) {
      this.estimateFormGroup.enable({ emitEvent: false });
      this.estimateFormGroup.controls.markUpToggle.enable({ emitEvent: false });
    } else {
      this.estimateFormGroup.disable({ emitEvent: false });
      this.estimateFormGroup.controls.markUpToggle.disable({ emitEvent: false });
    }
  }
  //#region Reactive Recalculation Subscriptions

  /**
   * After a service call that returns an updated model, patch the form.
   * Uses { emitEvent: false } so programmatic patches don't re-trigger subscriptions.
   */
  private patchFormFromModel(updatedModel: EstimateUiModel): void {
    this.patchFormGroupFromModel(this.estimateFormGroup, updatedModel);
  }

  /**
   * Subscribes to global controls whose changes trigger a full recalculation
   * of all sell prices across every section.
   */
  private setupGlobalRecalcSubscriptions(): void {
    const form = this.estimateFormGroup;

    // MarkUp toggle — recalculate all when user switches GM ↔ MU mode
    form.controls.markUpToggle.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMarkUp: boolean) => {
        this.isMarkUpSelected = isMarkUp;
        const updatedModel = this.solEstimateService.onMarkUpToggleChanged(isMarkUp);
        this.patchFormFromModel(updatedModel);
      });

    // Pricing mode — recalculate all when user switches Fixed Margin ↔ Line Item ↔ Fixed Price
    // No distinctUntilChanged(): the $0 guard below can patch this control, and patches
    // bypass the stream, leaving the operator with a stale value that swallows a later
    // user selection of that same mode.
    form.controls.sellAs.controls.sellAsPricingMode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode: SolEstimatePricingMode) => {
        const updatedModel = this.solEstimateService.onPricingModeChanged(mode);
        this.patchFormFromModel(updatedModel);
        // Rebuild per-row subscriptions since the editable fields change with pricing mode
        this.tearDownRowSubscriptions();
        this.setupPerRowRecalcSubscriptions();
        if (mode !== null && mode !== undefined) {
          this.estimateLastModifiedSellPriceService.resetAllSellPriceFlags();
        }
      });

    // Sell As Calculated GM% — in Fixed Margin / Fixed Price mode, this is the
    // single global percentage that drives every row's calculation.
    const calculatedGroup = form.controls.sellAs.controls.calculated;

    //ToDo : Check if removing the takeUntil is causing issue also check if it is getting destroyed properly or not.
    calculatedGroup.controls.grossMarginPercentage.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val: number) => {
        if (!this.isValid(val)) {
          return;
        }
        const config = this.solEstimateService.getCurrentConfig();
        if (config?.isFixedPriceSelected === null) {
          return;
        }
        // Exactly 100 may be a partial entry (e.g. 100.1); it is clamped on focusout instead.
        if (val === CONSTANTS.HUNDRED) {
          return;
        }
        const updatedModel = this.solEstimateService.onSoldAsGMPercentChanged(val);
        this.patchFormFromModel(updatedModel);
      });

    calculatedGroup.controls.markUpPercentage.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val: number) => {
        if (!this.isValid(val)) {
          return;
        }
        const config = this.solEstimateService.getCurrentConfig();
        if (config?.isFixedPriceSelected === null) {
          return;
        }

        const updatedModel = this.solEstimateService.onSoldAsGMPercentChanged(val);
        this.patchFormFromModel(updatedModel);
      });

    // Sell-As Sell Price — in Fixed Price mode, when the user edits the sell price,
    // derive GM%/MU% and recalculate all rows accordingly.
    form.controls.sellAs.controls.sellAsSellPrice.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val: number) => {
        if (Number(val) === CONSTANTS.ZERO) {
          // User tried to enter a fixed price — switch to Fixed Price mode even though $0 is invalid
          const config = this.solEstimateService.getCurrentConfig();
          if (config?.isFixedPriceSelected !== true) {
            const updatedModel = this.solEstimateService.onPricingModeChanged(SolEstimatePricingMode.Fixed_Price);
            this.patchFormFromModel(updatedModel);
          }
          const previousSellPrice = this.solEstimateService.estimateModel?.sellAs?.sellAsSellPrice ?? CONSTANTS.ZERO;
          form.controls.sellAs.controls.sellAsSellPrice.patchValue(previousSellPrice, { emitEvent: false });
          this.solEstimateService.notifySellAsSellPriceUpdated(previousSellPrice);
          this.notificationMessageService.showError(
            this.translate.instant('SELL_AS_ZERO_SELL_PRICE_MESSAGE'),
            this.translate.instant('SELL_AS_ZERO_SELL_PRICE_TITLE')
          );
          return;
        }
        const updatedModel = this.solEstimateService.onSellAsSellPriceChanged(val);
        this.patchFormFromModel(updatedModel);
      });
  }

  /** Clamps a committed Sell As GM% of exactly 100 to 99.99 once the user leaves the field. */
  @HostListener('focusout')
  onFocusOut(): void {
    const gmControl = this.estimateFormGroup?.controls.sellAs.controls.calculated.controls.grossMarginPercentage;
    const config = this.solEstimateService.getCurrentConfig();
    if (!gmControl || config?.isFixedPriceSelected === null || Number(gmControl.value) !== CONSTANTS.HUNDRED) {
      return;
    }
    const updatedModel = this.solEstimateService.onSoldAsGMPercentChanged(CONSTANTS.NINETY_NINE_POINT_NINE_NINE);
    this.patchFormFromModel(updatedModel);
  }

  private setupPropertiesGmPercentSubscription(): void {
    this.commonDataService.propertiesGmPercentChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ value, hasChanged }) => this.applyPropertiesGmPercentChange(value, hasChanged));
  }

  private applyPropertiesGmPercentChange(propertiesGmPercent: number, hasChanged: boolean): void {
    if (!hasChanged || propertiesGmPercent === null || propertiesGmPercent === this.getCurrentCalculatedSellAsPercent()) {
      return;
    }

    let updatedModel = this.solEstimateService.getModel();
    if (this.solEstimateService.getCurrentConfig()?.isFixedPriceSelected !== false) {
      updatedModel = this.solEstimateService.onPricingModeChanged(SolEstimatePricingMode.Fixed_Margin);
      this.patchFormFromModel(updatedModel);
    }

    // Properties already stores the value in the active Sell As representation:
    // GM% when markup is off and MU% when markup is on. The estimate service
    // expects that same mode-specific value.
    updatedModel = this.solEstimateService.onSoldAsGMPercentChanged(propertiesGmPercent);
    this.patchFormFromModel(updatedModel);
  }

  private getCurrentCalculatedSellAsPercent(): number | null {
    const calculated = this.estimateFormGroup?.controls.sellAs?.controls.calculated;
    const isMarkupSelected = this.solEstimateService.getCurrentConfig()?.isMarkUpSelected === true;
    const calculatedPercent = isMarkupSelected
      ? calculated?.controls.markUpPercentage?.value
      : calculated?.controls.grossMarginPercentage?.value;

    if (calculatedPercent == null) {
      return null;
    }

    const numericValue = Number(calculatedPercent);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  /**
   * Subscribes to each row's grossMarginPercentage and markUpPercentage controls
   * so that when the user edits a percentage, the row's sell price, GM$, and the
   * counterpart percentage are recalculated immediately.
   *
   * Only active in Line Item mode (where per-row inputs are shown).
   */
  private setupPerRowRecalcSubscriptions(): void {
    this.tearDownRowSubscriptions();

    // Technology fee GM% is editable in ALL pricing modes (unlike other rows)
    this.setupTechnologyFeeSubscription();

    // Only subscribe to per-row changes in Line Item mode
    const config = this.solEstimateService.getCurrentConfig();
    if (config?.isFixedPriceSelected !== null) {
      return; // Fixed Margin or Fixed Price — per-row inputs are read-only
    }

    this.setupMaterialRowSubscriptions();
    this.setupLaborRowSubscriptions();
    this.setupSubContractorRowSubscriptions();
    this.setupMiscRiskTravelRowSubscriptions();
  }

  // ── Material rows ─────────────────────────────────────────────────

  private setupMaterialRowSubscriptions(): void {
    const categories: Array<'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial'> = [
      'siemensMaterial', 'outsideMaterial', 'mechanicalMaterial', 'electricalMaterial'
    ];
    const subRows: Array<'headerRow' | 'shippingAndHandling' | 'warranty'> = [
      'headerRow', 'shippingAndHandling', 'warranty'
    ];

    for (const category of categories) {
      const catGroup = this.estimateFormGroup.controls.materialCosts.controls[category];
      for (const subRow of subRows) {
        const rowGroup = catGroup.controls[subRow];
        this.subscribeToRowPercentage(rowGroup, (percent, isGMPercent) => {
          this.estimateLastModifiedSellPriceService.setMaterialSellPriceFlag(category, subRow, false);
          const updatedModel = this.solEstimateService.onMaterialRowPercentChanged(category, subRow, percent, isGMPercent);
          this.patchFormFromModel(updatedModel);
        });
        // Subscribe to sell price changes (Line Item mode) — derives GM% from sell price
        this.subscribeToRowSellPrice(rowGroup, (newSellPrice) => {
          this.estimateLastModifiedSellPriceService.setMaterialSellPriceFlag(category, subRow, true);
          const updatedModel = this.solEstimateService.onMaterialRowSellPriceChanged(category, subRow, newSellPrice);
          this.patchFormFromModel(updatedModel);
        });
      }
    }
  }

  // ── Labor rows ────────────────────────────────────────────────────

  private setupLaborRowSubscriptions(): void {
    for (const key of this.laborKeys) {
      const rowGroup = this.estimateFormGroup.controls.laborCosts.controls[key];
      this.subscribeToRowPercentage(rowGroup, (percent, isGMPercent) => {
        this.estimateLastModifiedSellPriceService.setLaborSellPriceFlag(key, false);
        const updatedModel = this.solEstimateService.onLaborRowPercentChanged(key, percent, isGMPercent);
        this.patchFormFromModel(updatedModel);
      });
      this.subscribeToRowSellPrice(rowGroup, (newSellPrice) => {
        this.estimateLastModifiedSellPriceService.setLaborSellPriceFlag(key, true);
        const updatedModel = this.solEstimateService.onLaborRowSellPriceChanged(key, newSellPrice);
        this.patchFormFromModel(updatedModel);
      });
    }
  }

  // ── Subcontractor rows ────────────────────────────────────────────

  private setupSubContractorRowSubscriptions(): void {
    const rows = this.estimateFormGroup.controls.subContractorCosts.controls.subContractorsCostsRow;
    for (let i = 0; i < rows.length; i++) {
      const rowGroup = rows.at(i);
      const index = i;
      this.subscribeToRowPercentage(rowGroup, (percent, isGMPercent) => {
        const updatedModel = this.solEstimateService.onSubContractorRowPercentChanged(index, percent, isGMPercent);
        this.patchFormFromModel(updatedModel);
      });
      this.subscribeToRowSellPrice(rowGroup, (newSellPrice) => {
        const updatedModel = this.solEstimateService.onSubContractorRowSellPriceChanged(index, newSellPrice);
        this.patchFormFromModel(updatedModel);
      });
    }
  }

  // ── Misc / Risk / Travel rows ─────────────────────────────────────

  private setupMiscRiskTravelRowSubscriptions(): void {
    const miscRows = this.estimateFormGroup.controls.miscRiskTravelCosts.controls.rows;
    this.subscribeToNamedRowArray(miscRows, true); // isMiscTravel = true

    const riskCostsGroup = this.estimateFormGroup.controls.miscRiskTravelCosts.controls.riskCosts;
    if (riskCostsGroup) {
      const riskRows = riskCostsGroup.controls.rows;
      this.subscribeToNamedRowArray(riskRows, false); // isMiscTravel = false (risk rows)
    }
  }

  private subscribeToNamedRowArray(
    rowsArray: FormArray<FormGroup<SolEstimateTableRowBaseExtended>>,
    isMiscTravel: boolean = true
  ): void {
    for (let i = 0; i < rowsArray.length; i++) {
      const rowGroup = rowsArray.at(i);
      const rowName = rowGroup.controls.name.value;
      this.subscribeToRowPercentage(rowGroup, (percent, isGMPercent) => {
        if (isMiscTravel) {
          this.estimateLastModifiedSellPriceService.setMiscTravelSellPriceFlag(rowName, false);
        } else {
          this.estimateLastModifiedSellPriceService.setRiskSellPriceFlag(rowName, false);
        }
        const updatedModel = this.solEstimateService.onMiscRiskTravelRowPercentChanged(rowName, percent, isGMPercent);
        this.patchFormFromModel(updatedModel);
      });
      this.subscribeToRowSellPrice(rowGroup, (newSellPrice) => {
        if (isMiscTravel) {
          this.estimateLastModifiedSellPriceService.setMiscTravelSellPriceFlag(rowName, true);
        } else {
          this.estimateLastModifiedSellPriceService.setRiskSellPriceFlag(rowName, true);
        }
        const updatedModel = this.solEstimateService.onMiscRiskTravelRowSellPriceChanged(rowName, newSellPrice);
        this.patchFormFromModel(updatedModel);
      });
    }
  }

  // ── Technology fee row (editable in ALL pricing modes) ────────────

  private setupTechnologyFeeSubscription(): void {
    const rowGroup = this.estimateFormGroup.controls.technologyFee;
    this.subscribeToRowPercentage(rowGroup, (percent, isGMPercent) => {
      const updatedModel = this.solEstimateService.onStandaloneRowPercentChanged('technologyFee', percent, isGMPercent);
      const edited = isGMPercent ? 'grossMarginPercentage' : 'markUpPercentage';
      this.patchFormFromModel({
        ...updatedModel,
        technologyFee: { ...updatedModel.technologyFee, [edited]: percent }
      });
    });
  }

  // ── Tax Rate rows ─────────────────────────────────────────────────

  /**
   * Subscribes to the `rate` control valueChanges on each row in both
   * taxMaterialRate and taxMaterialLaborRate form arrays.
   * When the user edits a tax rate, updates the metadata and recalculates.
   *
   * These subscriptions are active in ALL pricing modes (not just Line Item)
   * because tax rates always affect the estimate regardless of mode.
   */
  private setupTaxRateSubscriptions(): void {
    this.subscribeToTaxRateRows(this.estimateFormGroup.controls.taxMaterialRate.controls.taxRateRows);
    this.subscribeToTaxRateRows(this.estimateFormGroup.controls.taxMaterialLaborRate.controls.taxRateRows);
  }

  /** Drops subscriptions pointing at replaced tax rate controls and binds the current ones. */
  private resetTaxRateSubscriptions(): void {
    this.tearDownTaxRateSubscriptions();
    this.setupTaxRateSubscriptions();
  }

  private tearDownTaxRateSubscriptions(): void {
    for (const sub of this.taxRateSubscriptions) {
      sub.unsubscribe();
    }
    this.taxRateSubscriptions = [];
  }

  private subscribeToTaxRateRows(
    taxRateRows: FormArray<FormGroup<SolEstimateTableTaxRateRow>>
  ): void {
    for (let i = 0; i < taxRateRows.length; i++) {
      const rowGroup = taxRateRows.at(i);
      const index = i;
      const sub = rowGroup.controls.rate.valueChanges
        .pipe(takeUntil(this.destroy$), debounceTime(300))
        .subscribe((newRate: number) => {
          if (!this.isValid(newRate)) {
            return;
          }
          this.suppressMetaDataReaction = true;
          try {
            const updatedModel = this.solEstimateService.onTaxRateValueChanged(index, newRate);
            this.patchFormFromModel(updatedModel);
          } finally {
            this.suppressMetaDataReaction = false;
          }
        });
      this.taxRateSubscriptions.push(sub);
    }
  }

  // ── Shared subscription helper ────────────────────────────────────

  /**
   * Subscribes to both grossMarginPercentage and markUpPercentage valueChanges
   * on a single row FormGroup. Calls the provided handler with the parsed
   * numeric value and a flag indicating which field was edited.
   *
   * Uses debounceTime(300) to avoid rapid-fire recalculations while the user types.
   * The patchFormGroupFromModel uses { emitEvent: false } so programmatic patches
   * do NOT re-trigger these subscriptions (preventing infinite loops).
   *
   * No distinctUntilChanged(): patches bypass the stream, so the operator would keep
   * a stale "last emitted" value and swallow a later user edit back to that value
   * (e.g. sell price edit moves GM% 30 -> 45, then the user retypes 30).
   */
  private subscribeToRowPercentage(
    rowGroup: FormGroup<SolEstimateTableRowBase> | FormGroup<SolEstimateTableRowBaseExtended> | FormGroup<SolEstimateTableRowBaseExtendedSubContractor
    >,
    handler: (percent: number, isGMPercent: boolean) => void
  ): void {
    // GM% field
    const gmSub = rowGroup.controls.grossMarginPercentage.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val: number) => {
        if (!this.isValid(val)) {
          return;
        }
        handler(val, true);
      });
    this.rowSubscriptions.push(gmSub);

    // MU% field
    const muSub = rowGroup.controls.markUpPercentage.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val: number) => {
        if (!this.isValid(val)) {
          return;
        }
        handler(val, false);
      });
    this.rowSubscriptions.push(muSub);
  }

  /**
   * Subscribes to sellPrice valueChanges on a single row FormGroup.
   * Called only in Line Item mode when individual row sell prices are editable.
   * When user edits sell price, derives GM% and MU% from the new value.
   *
   * Circular dependency protection:
   *  1. patchFormGroupFromModel uses { emitEvent: false } — prevents patches from re-triggering subscriptions
   *  2. debounceTime(300) — stabilizes rapid user input
   *  When sell price changes → derives GM% → patches GM% with emitEvent: false → no re-trigger ✓
   *  When GM% changes → derives sell price → patches sell price with emitEvent: false → no re-trigger ✓
   *
   * distinctUntilChanged() is deliberately omitted — see subscribeToRowPercentage.
   */
  private subscribeToRowSellPrice(
    rowGroup: FormGroup<SolEstimateTableRowBase> | FormGroup<SolEstimateTableRowBaseExtended> | FormGroup<SolEstimateTableRowBaseExtendedSubContractor>,
    handler: (newSellPrice: number) => void
  ): void {
    const sellPriceSub = rowGroup.controls.sellPrice.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val: number) => {
        if (!this.isValid(val)) {
          return;
        }
        handler(val);
      });
    this.rowSubscriptions.push(sellPriceSub);
  }

  /**
   * helper to check if a value is a valid finite number.
   */
  private isValid(val: number): boolean {
    return val !== null && val !== undefined && Number.isFinite(val) && !Number.isNaN(val);
  }

  /**
   * Unsubscribes all per-row subscriptions.
   */
  private tearDownRowSubscriptions(): void {
    for (const sub of this.rowSubscriptions) {
      sub.unsubscribe();
    }
    this.rowSubscriptions = [];
  }

  //#endregion

  //#region FormGroup Creation from Model

  /**
   * Creates the full estimate FormGroup from a plain EstimateUiModel.
   * This lives in the component — services never create or touch FormGroups.
   */
  private createFormGroupFromModel(model: EstimateUiModel): FormGroup<SolEstimateFormGroup> {
    return this.fb.group<SolEstimateFormGroup>({
      markUpToggle: this.fb.control<boolean>(model.markUpToggle),
      materialCosts: this.createMaterialCostsFormGroup(model.materialCosts),
      laborCosts: this.createLaborCostsFormGroup(model.laborCosts),
      subContractorCosts: this.createSubContractorCostsFormGroup(model.subContractorCosts),
      miscRiskTravelCosts: this.createMiscRiskTravelCostsFormGroup(model.miscRiskTravelCosts),
      taxNotIncluded: this.createRowBaseFormGroup(model.taxNotIncluded),
      taxMaterialRate: this.createTaxRateFormGroup(model.taxMaterialRate),
      taxMaterialLaborRate: this.createTaxRateFormGroup(model.taxMaterialLaborRate),
      technologyFee: this.createRowBaseFormGroup(model.technologyFee),
      subTotals: this.createRowBaseFormGroup(model.subTotals),
      sellAs: this.createSellAsFormGroup(model.sellAs),
      selectionControl: this.fb.control<string>('', { updateOn: 'blur' }),
      descriptionControl: this.fb.control<string>('', { updateOn: 'blur' })
    });
  }

  //May not be used
  getSelectionName(): string {
    return this.commonDataService.getSQWebProject()?.itemName ?? CONSTANTS.EMPTY_STRING;
  }
  //May not be used
  getEstimateDescription(): string {
    return this.commonDataService.getSQWebProject()?.description ?? CONSTANTS.EMPTY_STRING;
  }

  // ── Row Base ───────────────────────────────────────────────────
  //ToDo : Change the dataType of all the form controls to number and remove the parsing in service methods. 
  private createRowBaseFormGroup(row: EstimateRowModel): FormGroup<SolEstimateTableRowBase> {
    return this.fb.group<SolEstimateTableRowBase>({
      cost: this.fb.control<number>(row.cost),
      escalate: this.fb.control<string>(row.escalate),
      tdcPercentage: this.fb.control<number>(row.tdcPercentage),
      grossMarginPercentage: this.fb.control<number>(row.grossMarginPercentage),
      markUpPercentage: this.fb.control<number>(row.markUpPercentage),
      grossMargin: this.fb.control<number>(row.grossMargin),
      sellPrice: this.fb.control<number>(row.sellPrice),
    });
  }

  private createExtendedRowBaseFormGroup(row: EstimateRowExtendedModel): FormGroup<SolEstimateTableRowBaseExtended> {
    return this.fb.group<SolEstimateTableRowBaseExtended>({
      name: this.fb.control<string>(row.name),
      cost: this.fb.control<number>(row.cost),
      escalate: this.fb.control<string>(row.escalate),
      tdcPercentage: this.fb.control<number>(row.tdcPercentage),
      grossMarginPercentage: this.fb.control<number>(row.grossMarginPercentage),
      markUpPercentage: this.fb.control<number>(row.markUpPercentage),
      grossMargin: this.fb.control<number>(row.grossMargin),
      sellPrice: this.fb.control<number>(row.sellPrice),
    });
  }

  private createSubContractorRowFormGroup(row: EstimateRowExtendedModelSubContractor): FormGroup<SolEstimateTableRowBaseExtendedSubContractor> {
    return this.fb.group<SolEstimateTableRowBaseExtendedSubContractor>({
      name: this.fb.control<string>(row.name),
      cost: this.fb.control<number>(row.cost),
      escalate: this.fb.control<string>(row.escalate),
      tdcPercentage: this.fb.control<number>(row.tdcPercentage),
      grossMarginPercentage: this.fb.control<number>(row.grossMarginPercentage),
      markUpPercentage: this.fb.control<number>(row.markUpPercentage),
      grossMargin: this.fb.control<number>(row.grossMargin),
      sellPrice: this.fb.control<number>(row.sellPrice),
      isSellPriceLastModified: this.fb.control<boolean>(row.isSellPriceLastModified),
    });
  }

  // ── Material ───────────────────────────────────────────────────

  private createMaterialTypeFormGroup(model: EstimateMaterialTypeModel): FormGroup<SolEstimateTableMaterialCostsRow> {
    return this.fb.group<SolEstimateTableMaterialCostsRow>({
      headerRow: this.createRowBaseFormGroup(model.headerRow),
      shippingAndHandling: this.createRowBaseFormGroup(model.shippingAndHandling),
      warranty: this.createRowBaseFormGroup(model.warranty),
      isSubsidyValuePresent: this.fb.control<boolean>(false),
    });
  }

  private createMaterialCostsFormGroup(model: EstimateMaterialCostsModel): FormGroup<SolEstimateTableMaterialCostsRowCollection> {
    return this.fb.group<SolEstimateTableMaterialCostsRowCollection>({
      materialCostsHeader: this.createRowBaseFormGroup(model.materialCostsHeader),
      siemensMaterial: this.createMaterialTypeFormGroup(model.siemensMaterial),
      outsideMaterial: this.createMaterialTypeFormGroup(model.outsideMaterial),
      mechanicalMaterial: this.createMaterialTypeFormGroup(model.mechanicalMaterial),
      electricalMaterial: this.createMaterialTypeFormGroup(model.electricalMaterial),
    });
  }

  // ── Labor ──────────────────────────────────────────────────────

  private createLaborCostsFormGroup(model: EstimateLaborCostsModel): FormGroup<SolEstimateTableLaborCosts> {
    return this.fb.group<SolEstimateTableLaborCosts>({
      laborCostsHeader: this.createRowBaseFormGroup(model.laborCostsHeader),
      projectManagement: this.createRowBaseFormGroup(model.projectManagement),
      engineering: this.createRowBaseFormGroup(model.engineering),
      networkEngineering: this.createRowBaseFormGroup(model.networkEngineering),
      qualificationEngineering: this.createRowBaseFormGroup(model.qualificationEngineering),
      cyberSecurityEngineering: this.createRowBaseFormGroup(model.cyberSecurityEngineering),
      energyEngineering: this.createRowBaseFormGroup(model.energyEngineering),
      electricalEngineering: this.createRowBaseFormGroup(model.electricalEngineering),
      graphics: this.createRowBaseFormGroup(model.graphics),
      programming: this.createRowBaseFormGroup(model.programming),
      specialist: this.createRowBaseFormGroup(model.specialist),
      digitalDeploymentSpecialist: this.createRowBaseFormGroup(model.digitalDeploymentSpecialist),
      mechanical: this.createRowBaseFormGroup(model.mechanical),
      electrical: this.createRowBaseFormGroup(model.electrical),
    });
  }

  // ── Subcontractor ──────────────────────────────────────────────

  private createSubContractorCostsFormGroup(model: EstimateSubContractorCostsModel): FormGroup<SolEstimateTableSubContractorsCosts> {
    const rows = model.subContractorsCostsRow.map(r => this.createSubContractorRowFormGroup(r));
    return this.fb.group<SolEstimateTableSubContractorsCosts>({
      headerRow: this.createRowBaseFormGroup(model.headerRow),
      subContractorsCostsRow: this.fb.array<FormGroup<SolEstimateTableRowBaseExtendedSubContractor>>(rows),
    });
  }

  // ── Misc / Risk / Travel ───────────────────────────────────────

  private createMiscRiskTravelCostsFormGroup(model: EstimateMiscRiskTravelCostsModel): FormGroup<SolEstimateTableMiscRiskTravelCosts> {
    const rows = model.rows.map((row) => this.createExtendedRowBaseFormGroup(row));

    const result: SolEstimateTableMiscRiskTravelCosts = {
      headerRow: this.createExtendedRowBaseFormGroup(model.headerRow),
      rows: this.fb.array<FormGroup<SolEstimateTableRowBaseExtended>>(rows),
      showMechanicalFee: this.fb.control<boolean>(model.showMechanicalFee ?? true),
      showSubcontractorFee: this.fb.control<boolean>(model.showSubcontractorFee ?? true),
    };

    if (model.riskCosts) {
      const riskRows = model.riskCosts.rows.map(r => this.createExtendedRowBaseFormGroup(r));
      result.riskCosts = this.fb.group<SolEstimateTableMiscRiskTravelCosts>({
        headerRow: this.createExtendedRowBaseFormGroup(model.riskCosts.headerRow),
        rows: this.fb.array<FormGroup<SolEstimateTableRowBaseExtended>>(riskRows),
        showRiskCostsAccordion: this.fb.control<boolean>(model.riskCosts.showRiskCostsAccordion ?? false),
      });
    }

    return this.fb.group<SolEstimateTableMiscRiskTravelCosts>(result);
  }

  // ── Tax Rate ───────────────────────────────────────────────────

  private createTaxRateFormGroup(model: EstimateTaxRateModel): FormGroup<SolEstimateTableTaxRate> {
    const rows = model.taxRateRows.map(r =>
      this.fb.group<SolEstimateTableTaxRateRow>({
        name: this.fb.control<string>(r.name),
        rate: this.fb.control<number>(r.rate),
        cost: this.fb.control<number>(r.cost),
        tdcPercentage: this.fb.control<number>(r.tdcPercentage),
        sellPrice: this.fb.control<number>(r.sellPrice),
      })
    );
    return this.fb.group<SolEstimateTableTaxRate>({
      headerRow: this.fb.group<SolEstimateTableTaxRateHeaderRow>({
        description: this.fb.control<string>(model.headerRow.description),
        cost: this.fb.control<number>(model.headerRow.cost),
        tdcPercentage: this.fb.control<number>(model.headerRow.tdcPercentage),
        sellPrice: this.fb.control<number>(model.headerRow.sellPrice),
      }),
      taxRateRows: this.fb.array<FormGroup<SolEstimateTableTaxRateRow>>(rows),
    });
  }

  // ── Sell As ────────────────────────────────────────────────────

  private createSellAsFormGroup(model: EstimateSellAsGroupModel): FormGroup<SolEstimateTableSellAsGroup> {
    return this.fb.group<SolEstimateTableSellAsGroup>({
      calculated: this.fb.group<SolEstimateTableSellAsBase>({
        markUpPercentage: this.fb.control<number>(model.calculated.markUpPercentage),
        grossMarginPercentage: this.fb.control<number>(model.calculated.grossMarginPercentage),
      }),
      reported: this.fb.group<SolEstimateTableSellAsBase>({
        markUpPercentage: this.fb.control<number>(model.reported.markUpPercentage),
        grossMarginPercentage: this.fb.control<number>(model.reported.grossMarginPercentage),
      }),
      sellAsPricingMode: this.fb.control<SolEstimatePricingMode>(model.sellAsPricingMode),
      sellAsSellPrice: this.fb.control<number>(model.sellAsSellPrice),
    });
  }

  //#endregion

  //#region Model → FormGroup Patch

  /**
   * Patches the entire FormGroup from the model.
   * Uses { emitEvent: false } to prevent triggering valueChanges subscriptions.
   */
  private patchFormGroupFromModel(form: FormGroup<SolEstimateFormGroup>, model: EstimateUiModel): void {
    form.patchValue({
      markUpToggle: model.markUpToggle,
      taxNotIncluded: model.taxNotIncluded,
      technologyFee: model.technologyFee,
      subTotals: model.subTotals,
    }, { emitEvent: false });

    this.patchMaterialCosts(form.controls.materialCosts, model.materialCosts);
    this.patchLaborCosts(form.controls.laborCosts, model.laborCosts);
    this.patchSubContractorCosts(form.controls.subContractorCosts, model.subContractorCosts);
    this.patchMiscRiskTravelCosts(form.controls.miscRiskTravelCosts, model.miscRiskTravelCosts);
    this.patchTaxRate(form.controls.taxMaterialRate, model.taxMaterialRate);
    this.patchTaxRate(form.controls.taxMaterialLaborRate, model.taxMaterialLaborRate);

    form.controls.sellAs.patchValue({
      calculated: model.sellAs.calculated,
      reported: model.sellAs.reported,
      sellAsPricingMode: model.sellAs.sellAsPricingMode,
      sellAsSellPrice: model.sellAs.sellAsSellPrice,
    }, { emitEvent: false });

  }

  private patchMaterialCosts(
    fg: FormGroup<SolEstimateTableMaterialCostsRowCollection>,
    model: EstimateMaterialCostsModel
  ): void {
    const categories: Array<'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial'> = [
      'siemensMaterial', 'outsideMaterial', 'mechanicalMaterial', 'electricalMaterial'
    ];
    const subRows: Array<'headerRow' | 'shippingAndHandling' | 'warranty'> = [
      'headerRow', 'shippingAndHandling', 'warranty'
    ];

    (fg.controls.materialCostsHeader as FormGroup<SolEstimateTableRowBase>)
      .patchValue(model.materialCostsHeader, { emitEvent: false });

    for (const cat of categories) {
      for (const sub of subRows) {
        fg.controls[cat].controls[sub].patchValue(model[cat][sub], { emitEvent: false });
      }
    }
  }

  private patchLaborCosts(
    fg: FormGroup<SolEstimateTableLaborCosts>,
    model: EstimateLaborCostsModel
  ): void {
    (fg.controls.laborCostsHeader as FormGroup<SolEstimateTableRowBase>)
      .patchValue(model.laborCostsHeader, { emitEvent: false });

    for (const key of this.laborKeys) {
      fg.controls[key].patchValue(model[key], { emitEvent: false });
    }
  }

  private patchSubContractorCosts(
    fg: FormGroup<SolEstimateTableSubContractorsCosts>,
    model: EstimateSubContractorCostsModel
  ): void {
    (fg.controls.headerRow as FormGroup<SolEstimateTableRowBase>)
      .patchValue(model.headerRow, { emitEvent: false });

    const formArray = fg.controls.subContractorsCostsRow;

    if (formArray.length !== model.subContractorsCostsRow.length) {
      formArray.clear({ emitEvent: false });
      for (const row of model.subContractorsCostsRow) {
        formArray.push(this.createSubContractorRowFormGroup(row), { emitEvent: false });
      }
    } else {
      for (let i = 0; i < model.subContractorsCostsRow.length; i++) {
        formArray.at(i).patchValue(model.subContractorsCostsRow[i], { emitEvent: false });
      }
    }
  }

  private patchMiscRiskTravelCosts(
    fg: FormGroup<SolEstimateTableMiscRiskTravelCosts>,
    model: EstimateMiscRiskTravelCostsModel
  ): void {
    fg.controls.headerRow.patchValue(model.headerRow, { emitEvent: false });

    if (model.showMechanicalFee != null) {
      fg.controls.showMechanicalFee?.patchValue(model.showMechanicalFee, { emitEvent: false });
    }

    if (model.showSubcontractorFee != null) {
      fg.controls.showSubcontractorFee?.patchValue(model.showSubcontractorFee, { emitEvent: false });
    }

    const miscArray = fg.controls.rows;
    if (miscArray) {
      // Reconcile in place to avoid dangling subscriptions on old FormGroups
      for (let i = 0; i < Math.min(miscArray.length, model.rows.length); i++) {
        miscArray.at(i).patchValue(model.rows[i], { emitEvent: false });
      }
      // Remove excess rows from the end
      while (miscArray.length > model.rows.length) {
        miscArray.removeAt(miscArray.length - 1, { emitEvent: false });
      }
      // Add new rows that don't have existing FormGroups
      for (let i = miscArray.length; i < model.rows.length; i++) {
        miscArray.push(this.createExtendedRowBaseFormGroup(model.rows[i]), { emitEvent: false });
      }
    }

    const riskCostsGroup = fg.controls.riskCosts;
    if (riskCostsGroup && model.riskCosts) {
      riskCostsGroup.controls.headerRow.patchValue(model.riskCosts.headerRow, { emitEvent: false });
      const riskArray = riskCostsGroup.controls.rows;
      for (let i = 0; i < Math.min(riskArray.length, model.riskCosts.rows.length); i++) {
        riskArray.at(i).patchValue(model.riskCosts.rows[i], { emitEvent: false });
      }
      if (model.riskCosts.showRiskCostsAccordion != null) {
        riskCostsGroup.controls.showRiskCostsAccordion?.patchValue(model.riskCosts.showRiskCostsAccordion, { emitEvent: false });
      }
    }
  }

  private patchTaxRate(
    fg: FormGroup<SolEstimateTableTaxRate>,
    model: EstimateTaxRateModel
  ): void {
    fg.controls.headerRow.patchValue(model.headerRow, { emitEvent: false });

    const formArray = fg.controls.taxRateRows;
    if (formArray.length !== model.taxRateRows.length) {
      formArray.clear({ emitEvent: false });
      for (const row of model.taxRateRows) {
        formArray.push(this.fb.group<SolEstimateTableTaxRateRow>({
          name: this.fb.control<string>(row.name),
          rate: this.fb.control<number>(row.rate),
          cost: this.fb.control<number>(row.cost),
          tdcPercentage: this.fb.control<number>(row.tdcPercentage),
          sellPrice: this.fb.control<number>(row.sellPrice),
        }), { emitEvent: false });
      }
    } else {
      for (let i = 0; i < model.taxRateRows.length; i++) {
        formArray.at(i).patchValue(model.taxRateRows[i], { emitEvent: false });
      }
    }
  }

  //#endregion

  onEstimateNameAndDescChange(field: string, newValue: string): void {
    const project = this.commonDataService.getSQWebProject();
    if (!project) {
      return;
    }
    if (field == "estimateName") {
      project.itemName = newValue;
      this.commonDataService.patchProject(project);
    }
    else if (field == "description") {
      project.description = newValue;
      this.commonDataService.patchProject(project);
    }
  }

  private subscribeToEstimateNameAndDescChanges(): void {

    this.commonDataService.estimateProjectNameAndDescChanged$.pipe(takeUntil(this.destroy$)).subscribe((estimateData) => {
      if (estimateData) {
        if (estimateData.estimateName != null) {
          this.selectionControl.setValue(estimateData.estimateName, { emitEvent: false });
          this.previousValidEstimateName = estimateData.estimateName;
        }
        if (estimateData.description != null) {
          this.descriptionControl.setValue(estimateData.description, { emitEvent: false });
          this.previousValidDescription = estimateData.description;
        }
      }
      else {
        this.selectionControl.setValue('', { emitEvent: false });
        this.descriptionControl.setValue('', { emitEvent: false });
      }
    });
    this.selectionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string | null) => {
      this.validateAndPersistText(this.selectionControl, 'estimateName', value ?? '');
    });
    this.descriptionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string | null) => {
      this.validateAndPersistText(this.descriptionControl, 'description', value ?? '');
    });
  }

  private validateAndPersistText(control: FormControl<string>, field: 'estimateName' | 'description', value: string): void {
    const previousValue = field === 'estimateName'
      ? this.previousValidEstimateName
      : this.previousValidDescription;
    const nextValue = this.nameValidationService.validateAndCleanName(value, previousValue, field);

    if (nextValue !== value) {
      control.setValue(nextValue, { emitEvent: false });
    }
    if (field === 'estimateName') {
      this.previousValidEstimateName = nextValue;
    } else {
      this.previousValidDescription = nextValue;
    }
    this.onEstimateNameAndDescChange(field, nextValue);
  }

  private setupSapSubmittedSubscription(): void {
    this.commonDataService.isSapSubmitted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isSubmitted: boolean) => {
        const isEstimateOpen = !!this.commonDataService.getEstimateData();

        // Never enable when estimate is closed.
        const shouldDisable = isSubmitted || !isEstimateOpen;
        if (shouldDisable) {
          this.selectionControl.disable({ emitEvent: false });
          this.descriptionControl.disable({ emitEvent: false });
        } else {
          this.selectionControl.enable({ emitEvent: false });
          this.descriptionControl.enable({ emitEvent: false });
        }
      });
  }


  ngOnDestroy(): void {
    this.tearDownRowSubscriptions();
    this.tearDownTaxRateSubscriptions();

    this.taxRateSubscriptions = [];
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
}
